import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import { cn } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Receipt,
  Upload,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  ChevronRight,
  Settings2,
  Plus,
  Trash2,
  ShoppingCart,
  Cloud,
} from 'lucide-react'
import type { Invoice, InvoiceStatus, InvoicePortal, InvoicePortalType } from '@shared/types'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  PageHeader,
  StatusBadge,
  dateColumn,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'

// ─── Scan Status Types ──────────────────────────────────────────────────────

interface ScanSourceStatus {
  source: string
  label: string
  status: 'pending' | 'running' | 'done' | 'error' | 'skipped'
  processed?: number
  found?: number
  error?: string
}

interface ScanState {
  running: boolean
  startedAt: string | null
  finishedAt: string | null
  sources: ScanSourceStatus[]
}

const SOURCE_STATUS_ICON: Record<string, { icon: typeof Clock; color: string }> = {
  pending: { icon: Clock, color: 'text-gray-400' },
  running: { icon: Loader2, color: 'text-brand-500' },
  done: { icon: CheckCircle, color: 'text-green-500' },
  error: { icon: XCircle, color: 'text-red-500' },
  skipped: { icon: Clock, color: 'text-gray-300' },
}

function ScanStatusPanel({ scanState, onDismiss }: { scanState: ScanState; onDismiss: () => void }) {
  const allDone = !scanState.running && scanState.finishedAt
  const totalProcessed = scanState.sources.reduce((sum, s) => sum + (s.processed ?? 0), 0)
  const hasErrors = scanState.sources.some((s) => s.status === 'error')

  return (
    <Card
      className={cn(
        'min-w-[280px]',
        hasErrors && allDone ? 'border-red-200' : allDone ? 'border-green-200' : 'border-brand-200',
      )}
    >
      <Card.Body className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-body font-medium text-gray-800">
            {scanState.running ? 'Abruf läuft …' : 'Abruf abgeschlossen'}
          </span>
          {allDone ? (
            <Button variant="ghost" size="xs" onClick={onDismiss}>
              Schließen
            </Button>
          ) : null}
        </div>
        <div className="space-y-1">
          {scanState.sources.map((src) => {
            const cfg = SOURCE_STATUS_ICON[src.status] ?? SOURCE_STATUS_ICON.pending
            const Icon = cfg.icon
            return (
              <div key={src.source} className="flex items-center gap-2 text-body">
                <Icon
                  className={cn(
                    'w-4 h-4 flex-shrink-0',
                    cfg.color,
                    src.status === 'running' && 'animate-spin',
                  )}
                />
                <span className="text-gray-700">{src.label}</span>
                {src.status === 'done' ? (
                  <span className="text-caption text-gray-400 ml-auto">
                    {src.processed ?? 0} neu{src.found != null ? ` / ${src.found} gefunden` : ''}
                  </span>
                ) : null}
                {src.status === 'error' ? (
                  <span className="text-caption text-red-500 ml-auto truncate max-w-[150px]" title={src.error}>
                    Fehler
                  </span>
                ) : null}
                {src.status === 'running' ? (
                  <span className="text-caption text-brand-500 ml-auto">läuft</span>
                ) : null}
              </div>
            )
          })}
        </div>
        {allDone ? (
          <div
            className={cn(
              'text-caption pt-1 border-t',
              hasErrors ? 'text-red-600 border-red-100' : 'text-green-600 border-green-100',
            )}
          >
            {hasErrors
              ? 'Abruf mit Fehlern abgeschlossen'
              : `${totalProcessed} neue Rechnungen importiert`}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  )
}

// ─── Portal Management Panel ────────────────────────────────────────────────

const PORTAL_TYPES: { value: InvoicePortalType; label: string; icon: typeof ShoppingCart }[] = [
  { value: 'amazon', label: 'Amazon Business', icon: ShoppingCart },
  { value: 'also', label: 'ALSO Cloud Marketplace', icon: Cloud },
]

function PortalManagementPanel({ onClose }: { onClose: () => void }) {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const [newType, setNewType] = useState<InvoicePortalType>('amazon')
  const [newLabel, setNewLabel] = useState('')
  const [supplierSearch, setSupplierSearch] = useState('')

  const { data: portals = [], isLoading } = useQuery({
    queryKey: ['invoice-portals'],
    queryFn: () => apiClient.get('/invoices/portals').then((r) => r.data as InvoicePortal[]),
  })

  const { data: suppliers = [] } = useQuery({
    queryKey: ['sap-suppliers', supplierSearch],
    queryFn: () =>
      apiClient
        .get('/invoices/sap/suppliers', { params: { search: supplierSearch } })
        .then((r) => r.data?.data ?? []),
    enabled: supplierSearch.length >= 2,
  })

  const createPortal = useMutation({
    mutationFn: (body: { portalType: string; label: string }) =>
      apiClient.post('/invoices/portals', body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-portals'] })
      setNewLabel('')
    },
  })

  const updatePortal = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      sapCardCode?: string | null
      sapCardName?: string | null
      isActive?: boolean
      config?: Record<string, unknown>
    }) => apiClient.patch(`/invoices/portals/${id}`, body).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice-portals'] }),
  })

  const deletePortal = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/invoices/portals/${id}`).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice-portals'] }),
  })

  return (
    <Card>
      <Card.Header>
        <Card.Title>Portale & Kreditoren</Card.Title>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Schließen
        </Button>
      </Card.Header>
      <Card.Body className="space-y-5">
        <p className="text-caption text-gray-500">
          Jedes Portal wird automatisch einem SAP-Kreditor zugeordnet. Rechnungen aus diesem Portal
          werden dann direkt dem hinterlegten Lieferanten zugewiesen.
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-body text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Laden…
          </div>
        ) : portals.length === 0 ? (
          <EmptyState title="Noch keine Portale konfiguriert" size="sm" />
        ) : (
          <div className="space-y-3">
            {portals.map((portal) => {
              const typeCfg = PORTAL_TYPES.find((t) => t.value === portal.portalType)
              const Icon = typeCfg?.icon ?? ShoppingCart
              return (
                <div key={portal.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <span className="text-body font-medium text-gray-800">{portal.label}</span>
                      <span className="text-caption text-gray-400">({typeCfg?.label})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={portal.isActive ? 'success' : 'neutral'}
                        onClick={() =>
                          updatePortal.mutate({ id: portal.id, isActive: !portal.isActive })
                        }
                        className="cursor-pointer"
                      >
                        {portal.isActive ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                      <IconButton
                        icon={<Trash2 />}
                        size="xs"
                        variant="danger"
                        aria-label="Portal löschen"
                        onClick={() => {
                          if (confirm('Portal wirklich löschen?')) deletePortal.mutate(portal.id)
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-caption text-gray-500 w-24 flex-shrink-0">
                      SAP-Kreditor:
                    </label>
                    {portal.sapCardCode ? (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-body font-mono text-gray-700">
                          {a.code(portal.sapCardCode)}
                        </span>
                        {portal.sapCardName ? (
                          <span className="text-caption text-gray-400">
                            {a.name(portal.sapCardName)}
                          </span>
                        ) : null}
                        <Button
                          variant="linkDanger"
                          size="xs"
                          className="ml-auto"
                          onClick={() =>
                            updatePortal.mutate({
                              id: portal.id,
                              sapCardCode: null,
                              sapCardName: null,
                            })
                          }
                        >
                          Entfernen
                        </Button>
                      </div>
                    ) : (
                      <div className="flex-1">
                        <Input
                          placeholder="Lieferant suchen…"
                          value={supplierSearch}
                          onChange={(e) => setSupplierSearch(e.currentTarget.value)}
                        />
                        {suppliers.length > 0 && supplierSearch.length >= 2 ? (
                          <div className="mt-1 border border-gray-200 rounded-md max-h-32 overflow-y-auto">
                            {(suppliers as { CardCode: string; CardName: string }[])
                              .slice(0, 8)
                              .map((s) => (
                                <button
                                  key={s.CardCode}
                                  type="button"
                                  onClick={() => {
                                    updatePortal.mutate({
                                      id: portal.id,
                                      sapCardCode: s.CardCode,
                                      sapCardName: s.CardName,
                                    })
                                    setSupplierSearch('')
                                  }}
                                  className="w-full text-left px-2 py-1 text-body hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span className="font-mono text-gray-600">
                                    {a.code(s.CardCode)}
                                  </span>
                                  <span className="text-gray-700">{a.name(s.CardName)}</span>
                                </button>
                              ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="border-t border-gray-100 pt-4">
          <p className="text-caption font-medium text-gray-600 mb-2">Neues Portal hinzufügen</p>
          <div className="flex items-center gap-2">
            <select
              value={newType}
              onChange={(e) => setNewType(e.currentTarget.value as InvoicePortalType)}
              className="px-2 py-2 border border-gray-300 rounded-md text-body bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              {PORTAL_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <Input
              wrapperClassName="flex-1"
              value={newLabel}
              onChange={(e) => setNewLabel(e.currentTarget.value)}
              placeholder="Bezeichnung (z.B. Amazon Hauptkonto)"
            />
            <Button
              leadingIcon={<Plus />}
              size="sm"
              disabled={!newLabel || createPortal.isPending}
              onClick={() =>
                newLabel && createPortal.mutate({ portalType: newType, label: newLabel })
              }
            >
              Hinzufügen
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'pending', label: 'Ausstehend' },
  { value: 'extracted', label: 'Erkannt' },
  { value: 'matched', label: 'Zugeordnet' },
  { value: 'approved', label: 'Freigegeben' },
  { value: 'booked', label: 'Gebucht' },
  { value: 'rejected', label: 'Abgelehnt' },
]

export function InvoicesPage() {
  const a = useAnonymize()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [scanState, setScanState] = useState<ScanState | null>(null)
  const [showScanPanel, setShowScanPanel] = useState(false)
  const [showPortals, setShowPortals] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['invoices', statusFilter],
    queryFn: () => {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : ''
      return apiClient.get(`/invoices${params}`).then((r) => r.data)
    },
  })

  const { data: stats } = useQuery({
    queryKey: ['invoices', 'stats'],
    queryFn: () => apiClient.get('/invoices/stats').then((r) => r.data),
  })

  const pollStatus = useCallback(async () => {
    try {
      const { data: status } = await apiClient.get('/invoices/scan-status')
      setScanState(status)
      if (!status.running && status.finishedAt) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        queryClient.invalidateQueries({ queryKey: ['invoices'] })
      }
    } catch {
      // ignore polling errors
    }
  }, [queryClient])

  useEffect(() => {
    pollStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (scanState && (scanState.running || scanState.finishedAt)) {
      setShowScanPanel(true)
    }
  }, [scanState])

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  const startScan = useMutation({
    mutationFn: () => apiClient.post('/invoices/scan-all', {}).then((r) => r.data),
    onSuccess: (result) => {
      if (result.started) {
        setScanState(result.status)
        setShowScanPanel(true)
        if (pollingRef.current) clearInterval(pollingRef.current)
        pollingRef.current = setInterval(pollStatus, 2000)
      }
    },
  })

  const uploadInvoice = useMutation({
    mutationFn: async (file: File) => {
      const buffer = await file.arrayBuffer()
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
      )
      return apiClient
        .post('/invoices/upload', {
          fileName: file.name,
          fileBase64: base64,
          mimeType: file.type || 'application/pdf',
        })
        .then((r) => r.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast({ title: 'Rechnung erfolgreich erkannt', tone: 'success' })
    },
    onError: () => toast({ title: 'Upload fehlgeschlagen', tone: 'danger' }),
  })

  const invoices: Invoice[] = data?.data ?? []
  const total: number = data?.total ?? 0

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadInvoice.mutate(file)
      e.target.value = ''
    }
  }

  const pendingCount =
    (stats?.pending?.count ?? 0) + (stats?.extracted?.count ?? 0) + (stats?.matched?.count ?? 0)
  const bookedCount = stats?.booked?.count ?? 0
  const errorCount = (stats?.error?.count ?? 0) + (stats?.rejected?.count ?? 0)
  const isScanning = scanState?.running ?? false

  const columns: ColumnDef<Invoice, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: ({ getValue }) => <StatusBadge status={getValue() as InvoiceStatus} />,
      },
      {
        accessorKey: 'invoiceNumber',
        header: 'Rechnungsnr.',
        cell: ({ getValue }) => (
          <span className="font-mono text-gray-700">
            {(getValue() as string | null)
              ? a.docNum(getValue() as string)
              : '–'}
          </span>
        ),
      },
      {
        id: 'supplier',
        header: 'Lieferant',
        cell: ({ row }) => {
          const inv = row.original
          return (
            <span>
              <span className="font-medium text-gray-800">
                {inv.supplierName ? a.name(inv.supplierName) : 'Unbekannt'}
              </span>
              {inv.supplierCardCode ? (
                <span className="ml-1 text-caption text-gray-400">
                  ({a.code(inv.supplierCardCode)})
                </span>
              ) : null}
            </span>
          )
        },
      },
      dateColumn<Invoice>('invoiceDate', 'Datum', { size: 110 }),
      {
        accessorKey: 'grossAmount',
        header: 'Brutto',
        size: 120,
        cell: ({ getValue }) => {
          const v = getValue() as number | null | undefined
          return v != null ? (
            <span className="text-right block tabular-nums font-medium text-gray-800">
              {a.fmtCurrency(v / 100)}
            </span>
          ) : (
            '–'
          )
        },
      },
      {
        accessorKey: 'source',
        header: 'Quelle',
        size: 100,
        cell: ({ getValue }) => {
          const s = getValue() as string
          return (
            <span className="text-gray-500 capitalize">
              {s === 'email'
                ? 'E-Mail'
                : s === 'portal_amazon'
                  ? 'Amazon'
                  : s === 'portal_also'
                    ? 'ALSO'
                    : 'Manuell'}
            </span>
          )
        },
      },
      textColumn<Invoice>('originalFileName', 'Datei'),
      {
        id: 'arrow',
        header: '',
        enableSorting: false,
        size: 36,
        cell: () => <ChevronRight className="w-4 h-4 text-gray-400" />,
      },
    ],
    [a],
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Receipt className="w-6 h-6 text-brand-700" />
            Rechnungseingang
            <span className="text-small text-gray-400 font-normal">{total} Rechnungen</span>
          </span>
        }
        actions={
          <>
            <Button
              variant="secondary"
              leadingIcon={isScanning || startScan.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              disabled={isScanning || startScan.isPending}
              onClick={() => startScan.mutate()}
            >
              {isScanning ? 'Abruf läuft …' : 'Abruf starten'}
            </Button>
            <Button
              variant={showPortals ? 'primary' : 'secondary'}
              leadingIcon={<Settings2 />}
              onClick={() => setShowPortals((v) => !v)}
            >
              Portale
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              leadingIcon={uploadInvoice.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
              disabled={uploadInvoice.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              PDF hochladen
            </Button>
          </>
        }
      />

      {showPortals ? <PortalManagementPanel onClose={() => setShowPortals(false)} /> : null}

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <Card.Body>
            <p className="text-small text-gray-500 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              Zur Freigabe
            </p>
            <p className="text-h1 font-bold text-indigo-600 mt-1">{pendingCount}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-small text-gray-500 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" />
              Gebucht
            </p>
            <p className="text-h1 font-bold text-green-600 mt-1">{bookedCount}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-small text-gray-500 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Fehler / Abgelehnt
            </p>
            <p className="text-h1 font-bold text-red-600 mt-1">{errorCount}</p>
          </Card.Body>
        </Card>
      </div>

      {/* Status-Filter */}
      <FilterBar>
        {STATUS_FILTERS.map((tab) => (
          <FilterChip
            key={tab.value}
            active={statusFilter === tab.value}
            onClick={() => setStatusFilter(tab.value)}
          >
            {tab.label}
          </FilterChip>
        ))}
      </FilterBar>

      {/* Tabelle */}
      <DataTable<Invoice>
        data={invoices}
        columns={columns}
        getRowId={(i) => i.id}
        loading={isLoading}
        onRetry={() => refetch()}
        onRowClick={(inv) => navigate(`/rechnungen/${inv.id}`)}
        emptyState={
          <EmptyState
            title="Keine Rechnungen gefunden"
            icon={<Receipt />}
            description="Lade eine PDF hoch oder starte einen Portal-Abruf."
          />
        }
      />

      {/* Scan Status Panel */}
      {showScanPanel && scanState && (scanState.running || scanState.finishedAt) ? (
        <div className="fixed bottom-4 right-4 z-sticky">
          <ScanStatusPanel scanState={scanState} onDismiss={() => setShowScanPanel(false)} />
        </div>
      ) : null}

      {/* Upload Indicator (kompakter Fallback neben Toast) */}
      {uploadInvoice.isPending ? (
        <div className="fixed bottom-4 right-4 z-sticky">
          <Card>
            <Card.Body className="flex items-center gap-3 py-2">
              <Loader2 className="w-5 h-5 animate-spin text-brand-500" />
              <span className="text-body text-gray-600">Rechnung wird verarbeitet …</span>
            </Card.Body>
          </Card>
        </div>
      ) : null}
    </div>
  )
}
